"use client";

import type { useChat } from "@ai-sdk/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import { UPDATE_SA_DAILY_EXPORT_SETTING_NAME } from "./constants";
import type { UpdateSaDailyExportSettingInput } from "./schema";

const DailyExportTargetQuery = graphql(`
  query SaDailyExportConfirm(
    $projectId: ID!
    $searchAnalyticsId: ID!
    $viewId: ID!
    $useProject: Boolean!
    $useSearchAnalytics: Boolean!
    $useView: Boolean!
  ) {
    project: node(id: $projectId) @include(if: $useProject) {
      __typename
      ... on SearchAnalyticsProject {
        searchAnalytics {
          id
          __typename
          spreadsheetID
        }
      }
    }
    searchAnalytics: node(id: $searchAnalyticsId)
      @include(if: $useSearchAnalytics) {
      __typename
      ... on SearchAnalytics {
        id
        spreadsheetID
      }
    }
    view: node(id: $viewId) @include(if: $useView) {
      __typename
      ... on SearchAnalyticsView {
        id
        name
        spreadsheetID
      }
    }
  }
`);

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function UpdateSaDailyExportSettingConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const isTarget =
    isToolUIPart(part) &&
    getToolName(part) === UPDATE_SA_DAILY_EXPORT_SETTING_NAME;
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<UpdateSaDailyExportSettingInput>;

  const viewId = input.viewId ?? "";
  const searchAnalyticsId = input.searchAnalyticsId ?? "";
  const projectId = input.projectId ?? "";

  const useView = Boolean(viewId);
  const useSearchAnalytics = Boolean(searchAnalyticsId) && !useView;
  const useProject = Boolean(projectId) && !useView && !useSearchAnalytics;

  const [{ data, fetching }] = useQuery({
    query: DailyExportTargetQuery,
    variables: {
      projectId,
      searchAnalyticsId,
      viewId,
      useProject,
      useSearchAnalytics,
      useView,
    },
    pause: !(useView || useSearchAnalytics || useProject),
  });

  if (!isTarget || !isInputAvailable) return null;

  let currentSpreadsheetId: string | null | undefined = undefined;
  let targetLabel = <Trans>Search Analytics</Trans>;
  let targetName: string | undefined;

  if (useView) {
    const view =
      data?.view?.__typename === "SearchAnalyticsView" ? data.view : null;
    currentSpreadsheetId = view?.spreadsheetID;
    targetLabel = <Trans>Search Analytics view</Trans>;
    targetName = view?.name ?? undefined;
  } else if (useSearchAnalytics) {
    const sa =
      data?.searchAnalytics?.__typename === "SearchAnalytics"
        ? data.searchAnalytics
        : null;
    currentSpreadsheetId = sa?.spreadsheetID;
    targetLabel = <Trans>Search Analytics (default view)</Trans>;
  } else if (useProject) {
    const project =
      data?.project?.__typename === "SearchAnalyticsProject"
        ? data.project
        : null;
    currentSpreadsheetId = project?.searchAnalytics?.spreadsheetID;
    targetLabel = <Trans>Search Analytics project (default view)</Trans>;
  }

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to update daily export settings?</Trans>
      </div>
      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>Target</Trans>:{" "}
          <span className="font-medium">{targetLabel}</span>
          {targetName ? (
            <span className="text-muted-foreground"> ({targetName})</span>
          ) : null}
        </li>
        <li>
          <Trans>Current spreadsheet ID</Trans>:{" "}
          {fetching ? (
            <Skeleton className="inline-block h-4 w-20 align-middle" />
          ) : (
            <span className="font-medium">
              {currentSpreadsheetId || <Trans>(not set)</Trans>}
            </span>
          )}
        </li>
        <li>
          <Trans>New spreadsheet ID</Trans>:{" "}
          <span className="font-medium">{input.spreadsheetId ?? ""}</span>
        </li>
      </ul>

      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={UPDATE_SA_DAILY_EXPORT_SETTING_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Update</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={UPDATE_SA_DAILY_EXPORT_SETTING_NAME}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
